import { useMemo } from 'react';

import { useLocations } from '@/core/hooks/use-locations';
import { useReplyPromptTemplates } from '@/core/hooks/use-reply-prompt-templates';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export type EffectiveReplyStyle = {
  /** Template name, or null for an own (custom) style / nothing configured. */
  templateName: string | null;
  kind: 'template' | 'custom' | 'none';
  /** Whether a location override (not the workspace default) is in effect. */
  isLocationOverride: boolean;
};

/**
 * Which reply style AI drafts use for a location: its own override if it has
 * one, otherwise the workspace default. Mirrors the backend resolution.
 */
export function useEffectiveReplyStyle(
  locationId: string | undefined,
): EffectiveReplyStyle {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { locations } = useLocations(workspaceId, { limit: 200 });
  const { data: templates } = useReplyPromptTemplates(workspaceId);

  return useMemo(() => {
    const locationConfig = locations.find(
      (l) => l.id === locationId,
    )?.replyPromptConfig;
    const hasContent = (c: typeof locationConfig) =>
      Boolean(c?.templateId || c?.tone || c?.considerations || c?.mustInclude);
    const isLocationOverride = hasContent(locationConfig);
    const config = isLocationOverride
      ? locationConfig
      : workspace?.replyPromptConfig;

    if (!hasContent(config)) {
      return { templateName: null, kind: 'none', isLocationOverride: false };
    }
    if (config?.templateId) {
      const name = templates?.find((t) => t.id === config.templateId)?.name;
      return {
        templateName: name ?? null,
        kind: 'template',
        isLocationOverride,
      };
    }
    return { templateName: null, kind: 'custom', isLocationOverride };
  }, [locations, locationId, templates, workspace?.replyPromptConfig]);
}
